import { CSSProperties, ReactElement, cloneElement } from 'react';
import { Box, Tooltip } from '@mui/material';

// Oval (pill-shaped) box used for the "Deactivated" hover text, matching the MUI tooltip colors
export const DEACTIVATED_PILL_SX = {
  borderRadius: '999px',
  px: 1.5,
  py: 0.5,
  fontSize: '12px',
  lineHeight: 1.2,
  color: '#FFFFFF',
  backgroundColor: 'rgba(97, 97, 97, 0.92)',
};

// sx for the element that reveals <DeactivatedHoverLabel /> while it is hovered (e.g. a name + role row)
export const DEACTIVATED_HOVER_AREA_SX = { '&:hover .deactivatedLabel': { display: 'inline-block' } };

// "Deactivated" oval box shown only while the surrounding DEACTIVATED_HOVER_AREA_SX element is hovered
export const DeactivatedHoverLabel = ({ ml = 1 }: { ml?: number }) => (
  <Box
    component="span"
    className="deactivatedLabel"
    sx={{ ...DEACTIVATED_PILL_SX, display: 'none', ml, alignSelf: 'center' }}
  >
    Deactivated
  </Box>
);

interface InactiveUserNameProps {
  isInactive?: boolean;
  // Set to false where "Deactivated" is already shown as text next to the name
  withTooltip?: boolean;
  // The element showing the user's name, e.g. <Typography>{name}</Typography>
  children: ReactElement<{ style?: CSSProperties }>;
}

// Strikes through the name of a deactivated firm user and shows a "Deactivated" pill tooltip on hover.
// Active users' names are rendered unchanged.
const InactiveUserName = ({ isInactive, withTooltip = true, children }: InactiveUserNameProps) => {
  if (!isInactive) {
    return children;
  }

  const struckName = cloneElement(children, {
    style: { ...children.props.style, textDecoration: 'line-through' },
  });

  return withTooltip ? (
    <Tooltip
      title="Deactivated"
      placement="top"
      componentsProps={{ tooltip: { sx: DEACTIVATED_PILL_SX } }}
    >
      {struckName}
    </Tooltip>
  ) : (
    struckName
  );
};

export default InactiveUserName;
